/* eslint-disable no-underscore-dangle */
import { ApolloError, useMutation } from '@apollo/client';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'usehooks-ts';

import {
  DeleteAdMutation,
  DeleteAdMutationVariables
} from '@/__generated_graphql__/graphql';
import { useSwitchActivateAdMutation } from '@/__generated_graphql__/types';
import { CardMenuOptions, CardMenuProps } from '@/components/Card/Card.types';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { ThreeDotsIcon } from '@/components/ui/Icons/Icons';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { CardStatus } from '@/constants/common';
import { DELETE_AD } from '@/graphql/mutations/ad.mutations';
import { GET_ADS_STATE_COUNTS, GET_PROFILE_ADS } from '@/graphql/queries';
import { useGraphQLErrorProcessor } from '@/hooks/useGraphQLErrorProcessor';
import { useTopProgressBar } from '@/providers/TopProgressBarProvider';
import { AdStatus } from '@/types';
import { capitalize } from '@/utils/helpers/capitalize';

import * as S from './Card.styles';

const { APPROVED, EXPIRED, REJECTED } = CardStatus;

export const CardMenu: FC<CardMenuProps> = ({
  id,
  subtitle,
  status,
  activeTab,
  adType,
  disabled = false
}) => {
  const { t } = useTranslation();
  const isDeactivated = status === AdStatus.expired;

  const [isOpen, setIsOpen] = useState(false);

  const [modalData, setModalData] = useState<{
    title: string;
    subtitle: string;
    confirmLabel: string;
    confirmDataCy?: string;
    onConfirm: () => Promise<void>;
  } | null>(null);

  const [switchStatusMutation, { loading: switchLoading }] =
    useSwitchActivateAdMutation();

  const [deleteMutation, { loading: deleteLoading }] = useMutation<
    DeleteAdMutation,
    DeleteAdMutationVariables
  >(DELETE_AD);

  const { track } = useTopProgressBar();
  const menuRef = useRef<HTMLDivElement>(null);
  const handleGraphQLErrors = useGraphQLErrorProcessor();

  const isDisabled = disabled || switchLoading || deleteLoading;

  const handleToggleMenu = () => {
    setIsOpen(prev => !prev);
  };

  const closeMenuAndModal = () => {
    setModalData(null);
    setIsOpen(false);
  };

  const handleCancel = () => {
    closeMenuAndModal();
  };

  const refetchQueries = [
    {
      query: GET_ADS_STATE_COUNTS,
      variables: {
        adType
      }
    },
    {
      query: GET_PROFILE_ADS,
      variables: {
        state: activeTab,
        first: 24,
        adType
      }
    }
  ];

  const handleDelete = async () => {
    if (!id) return;
    closeMenuAndModal();

    try {
      await track(async () =>
        deleteMutation({
          variables: {
            id,
            adType
          },
          refetchQueries,
          awaitRefetchQueries: true
        })
      );
    } catch (error) {
      handleGraphQLErrors({ error: error as ApolloError });
    }
  };

  const handleOpenDeleteModal = () => {
    setModalData({
      title: t('Card.delete_modal_title', { name: subtitle }),
      subtitle: t('Card.delete_modal_subtitle'),
      confirmLabel: t('Card.delete_button_label'),
      confirmDataCy: 'delete',
      onConfirm: handleDelete
    });
  };

  const formattedAdType = capitalize(adType as string);

  const handleExpire = async () => {
    if (!id) return;
    closeMenuAndModal();

    try {
      await track(async () =>
        switchStatusMutation({
          variables: {
            id,
            adType
          },
          refetchQueries,
          awaitRefetchQueries: true
        })
      );
    } catch (error) {
      handleGraphQLErrors({ error: error as ApolloError });
    }
  };

  const handleOpenExpireModal = () => {
    setModalData({
      title: t(`${formattedAdType}.confirm_deactivation_title`, {
        name: subtitle
      }),
      subtitle: t(`${formattedAdType}.confirm_deactivation_desc`),
      confirmLabel: t('Vacancy.deactivate_button_label'),
      confirmDataCy: 'expire',
      onConfirm: handleExpire
    });
  };

  const menuOptions: CardMenuOptions = {
    [APPROVED]: [
      {
        id: 'expire-button',
        label: 'Card.expire_label',
        onClick: handleOpenExpireModal
      }
    ],
    [EXPIRED]: [
      {
        id: 'delete-button',
        label: 'Card.delete_label',
        onClick: handleOpenDeleteModal
      }
    ],
    [REJECTED]: [
      {
        id: 'delete-button',
        label: 'Card.delete_label',
        onClick: handleOpenDeleteModal
      }
    ]
  };

  useOnClickOutside(menuRef, () => {
    setIsOpen(false);
  });

  const currentOptions = status ? menuOptions[status] : undefined;

  if (!currentOptions) return null;

  return (
    <S.MenuWrapper
      ref={menuRef}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <S.MenuButton
        data-cy="profile-card-menu-button"
        $isOpen={isOpen}
        onClick={handleToggleMenu}
        disabled={isDisabled}
      >
        <ThreeDotsIcon />
      </S.MenuButton>

      {isOpen && (
        <S.DropdownMenu>
          {currentOptions.map(option => (
            <S.DropdownItem
              data-cy={`profile-card-menu-${option.id}`}
              key={option.id}
              onClick={option.onClick}
            >
              <Typography variant="body2">{t(option.label)}</Typography>
            </S.DropdownItem>
          ))}
        </S.DropdownMenu>
      )}

      {modalData && (
        <ConfirmModal
          title={modalData.title}
          subtitle={modalData.subtitle}
          confirmDataCy={modalData.confirmDataCy}
          confirmLabel={modalData.confirmLabel}
          cancelLabel={t('Card.cancel_button_label')}
          onConfirm={modalData.onConfirm}
          onCancel={handleCancel}
          confirmButtonProps={{
            disabled: isDisabled,
            ...(isDeactivated ? { variant: 'remove' } : {})
          }}
          cancelButtonProps={{
            disabled: isDisabled
          }}
        />
      )}
    </S.MenuWrapper>
  );
};
